import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Heart, Globe, Sparkles, Briefcase, Baby } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = { heart: Heart, globe: Globe, sparkles: Sparkles, briefcase: Briefcase, baby: Baby };

const tarotStyles = {
  love: { symbol: '♥', accent: '#FF6B6B', gradient: 'from-red-950/90 via-red-900/50 to-black', pattern: 'radial-gradient(circle at 50% 30%, rgba(255,107,107,0.2) 0%, transparent 60%)', sage: '/sages/love.png', objectPosition: 'center 20%',
    backstory: {
      title: 'The Love Sage',
      epithet: 'Keeper of Hearts',
      story: "Born from a single starburst at the dawn of time, the Love Sage carries the cosmic heart-staff — a relic that pulses with every confession, every heartbreak, every wedding vow ever whispered into the universe. He has witnessed every great romance and every quiet ghosting. He doesn't judge. He listens. He's been a wingman to Cleopatra, a therapist to Romeo, and a silent witness to your 3am texts.",
      wisdom: 'Love is not a feeling. It is a choice you make every morning, even when the coffee is bad.',
    } },
  geopolitics: { symbol: '⚖', accent: '#4ECDC4', gradient: 'from-teal-950/90 via-teal-900/50 to-black', pattern: 'radial-gradient(circle at 50% 30%, rgba(78,205,196,0.2) 0%, transparent 60%)', sage: '/sages/geopolitics.png', objectPosition: 'center 25%',
    backstory: {
      title: 'The Geopolitics Sage',
      epithet: 'Architect of Treaties',
      story: "Older than the oldest border, this Sage has sat in every war room from Babylon to Brussels. He speaks 47 languages fluently and 200 more well enough to make a stubborn diplomat laugh. He has seen empires rise on hope and fall on hubris. He knows the difference between a strong stance and a stubborn one — and so should you.",
      wisdom: 'The map is never the territory. The territory is people, and people are messy.',
    } },
  pop: { symbol: '★', accent: '#FF69B4', gradient: 'from-pink-950/90 via-pink-900/50 to-black', pattern: 'radial-gradient(circle at 50% 30%, rgba(255,105,180,0.2) 0%, transparent 60%)', sage: '/sages/pop.png', objectPosition: 'center 25%',
    backstory: {
      title: 'The Pop Culture Sage',
      epithet: 'Oracle of the Algorithm',
      story: "He DJ'd at Studio 54, mosh-pitted at Woodstock, and ghostwrote a Drake verse you definitely know. He sees the strings between every viral moment — why one TikTok hits and another dies in 4 seconds. He's the reason your favorite indie band 'feels' mainstream all of a sudden. He doesn't gatekeep. He gatewatches.",
      wisdom: 'A trend is just a feeling that got organized. Vibe accordingly.',
    } },
  adulting: { symbol: '⚡', accent: '#FFD93D', gradient: 'from-yellow-950/90 via-yellow-900/50 to-black', pattern: 'radial-gradient(circle at 50% 30%, rgba(255,217,61,0.2) 0%, transparent 60%)', sage: '/sages/adulting.png', objectPosition: 'center 30%',
    backstory: {
      title: 'The Adulting Sage',
      epithet: 'Guardian of the Slow Burn',
      story: "She filed her first tax return in 1487 and has been quietly perfecting her credit score ever since. She doesn't believe in 'hustle culture' — she believes in compound interest, boundaries, and going to bed at 10pm. Her robe is stitched with reminders: Mindset. Finances. Career. Health. She's lived enough lives to know which one matters this week.",
      wisdom: 'Progress, not perfection. The bills don\'t care about your vibe, but rest will pay you back.',
    } },
  motherhood: { symbol: '❋', accent: '#C084FC', gradient: 'from-purple-950/90 via-purple-900/50 to-black', pattern: 'radial-gradient(circle at 50% 30%, rgba(192,132,252,0.2) 0%, transparent 60%)', sage: '/sages/motherhood.png', objectPosition: 'center 25%',
    backstory: {
      title: 'The Motherhood Sage',
      epithet: 'Carrier of the Eternal Flame',
      story: "She has rocked every kind of baby — colicky, sleeping, smiling, crying through the night for no reason at all. She has been the first mother, the last mother, and every mother in between. The golden halo behind her is not divinity; it is the warmth of three million bedtime stories. She knows the weight of doing it all and being told it's not enough. She still chooses tenderness.",
      wisdom: 'You are doing better than you think. The fact that you worry is the proof.',
    } },
};

export { tarotStyles };

// Exported for use in CommunityDebatePage
export const TarotCardMini = ({ community, size = 'sm' }) => {
  const Icon = iconMap[community.emoji] || Sparkles;
  const tarot = tarotStyles[community.slug] || tarotStyles.pop;
  const isSmall = size === 'sm';

  return (
    <div
      className={`${isSmall ? 'w-12 h-16' : 'w-16 h-22'} rounded-lg border border-white/10 overflow-hidden relative flex-shrink-0`}
      style={{ boxShadow: `0 0 12px ${tarot.accent}20` }}
      data-testid={`tarot-mini-${community.slug}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-b ${tarot.gradient}`} />
      {tarot.sage ? (
        <img src={tarot.sage} alt={community.name} className="absolute inset-0 w-full h-full object-cover opacity-90" style={{ objectPosition: tarot.objectPosition || 'center' }} />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      <span className="absolute top-1 left-1.5 text-[8px] opacity-60 z-10" style={{ color: tarot.accent, textShadow: `0 0 4px ${tarot.accent}` }}>{tarot.symbol}</span>
      {!tarot.sage && (
        <div className="relative flex flex-col items-center justify-center h-full">
          <Icon className={`${isSmall ? 'w-4 h-4' : 'w-5 h-5'}`} style={{ color: tarot.accent }} />
        </div>
      )}
    </div>
  );
};

const TarotCard = ({ community, isFront, offset, onClick }) => {
  const tarot = tarotStyles[community.slug] || tarotStyles.pop;

  // Stacked deck: center is front, others behind with horizontal offset
  const xShift = offset * 30;
  const scale = isFront ? 1 : 0.92 - Math.abs(offset) * 0.03;
  const zIndex = isFront ? 20 : 10 - Math.abs(offset);
  const blur = isFront ? 0 : Math.abs(offset) * 1.5;
  const cardOpacity = isFront ? 1 : 0.55 - Math.abs(offset) * 0.08;

  return (
    <motion.button
      onClick={onClick}
      layout
      animate={{
        x: xShift,
        scale,
        opacity: cardOpacity,
        filter: `blur(${blur}px)`,
      }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="absolute top-0 rounded-2xl border-2 overflow-hidden cursor-pointer"
      style={{
        width: '10.5rem',
        height: '15rem',
        zIndex,
        borderColor: isFront ? `${tarot.accent}50` : 'rgba(255,255,255,0.05)',
        boxShadow: isFront ? `0 0 30px ${tarot.accent}30, 0 8px 32px rgba(0,0,0,0.5)` : '0 4px 16px rgba(0,0,0,0.4)',
      }}
      whileHover={isFront ? { scale: 1.04, y: -4 } : {}}
      whileTap={isFront ? { scale: 0.97 } : {}}
      data-testid={`community-card-${community.slug}`}
    >
      {/* Sage image as main visual */}
      {tarot.sage && (
        <img src={tarot.sage} alt={community.name} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: tarot.objectPosition || 'center' }} draggable={false} />
      )}

      {/* Tarot gradient overlay — no top fade so heart/staff/key items stay sharp; bottom fade for name banner */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/95" />
      <div className="absolute inset-0" style={{ background: tarot.pattern, mixBlendMode: 'overlay' }} />

      {/* Decorative corners */}
      <span className="absolute top-3 left-3 text-base opacity-70 z-10" style={{ color: tarot.accent, textShadow: `0 0 8px ${tarot.accent}80` }}>{tarot.symbol}</span>
      <span className="absolute bottom-3 right-3 text-base opacity-70 rotate-180 z-10" style={{ color: tarot.accent, textShadow: `0 0 8px ${tarot.accent}80` }}>{tarot.symbol}</span>
      <div className="absolute inset-3 border border-white/10 rounded-xl pointer-events-none" />

      {/* Bottom name banner */}
      <div className="absolute bottom-0 left-0 right-0 px-3 pb-4 pt-12 z-10 text-center">
        <h3 className="font-heading text-base font-bold text-white tracking-wider" style={{ textShadow: `0 2px 8px rgba(0,0,0,0.9), 0 0 12px ${tarot.accent}60` }}>{community.name}</h3>
        <div className="w-8 h-px mx-auto mt-1.5 opacity-60" style={{ backgroundColor: tarot.accent }} />
      </div>

      {/* Bottom glow on front card */}
      {isFront && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1 z-20"
          style={{ backgroundColor: tarot.accent }}
          animate={{ opacity: [0.4, 0.9, 0.4] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
    </motion.button>
  );
};

const CommunitiesRow = () => {
  const [communities, setCommunities] = useState([]);
  const [centerIndex, setCenterIndex] = useState(0);
  const navigate = useNavigate();

  const fetchCommunities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/communities`);
      setCommunities(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:communities]', err?.message);
    }
  }, []);

  useEffect(() => { fetchCommunities(); }, [fetchCommunities]);

  const goLeft = () => setCenterIndex(prev => Math.max(0, prev - 1));
  const goRight = () => setCenterIndex(prev => Math.min(communities.length - 1, prev + 1));

  const handleCardClick = (index) => {
    if (index === centerIndex) {
      navigate(`/community/${communities[index].slug}`);
    } else {
      setCenterIndex(index);
    }
  };

  // Swipe handler: swipe left → next, swipe right → prev
  const handleDragEnd = (_e, info) => {
    const SWIPE_THRESHOLD = 50;
    const velocityThreshold = 300;
    const { offset, velocity } = info;

    if (offset.x < -SWIPE_THRESHOLD || velocity.x < -velocityThreshold) {
      goRight();
    } else if (offset.x > SWIPE_THRESHOLD || velocity.x > velocityThreshold) {
      goLeft();
    }
  };

  if (communities.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 sm:mt-10"
      data-testid="communities-section"
    >
      <h3 className="font-heading text-lg sm:text-xl font-bold text-zinc-300 mb-5 px-1">
        Communities
      </h3>

      {/* Card deck — swipeable */}
      <motion.div
        className="relative flex justify-center items-start touch-pan-y select-none"
        style={{ height: '16rem' }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        data-testid="communities-swipe-container"
      >
        {/* Cards - stacked */}
        <div className="relative" style={{ width: '10.5rem', height: '15rem' }}>
          {communities.map((c, i) => (
            <TarotCard
              key={c.slug}
              community={c}
              isFront={i === centerIndex}
              offset={i - centerIndex}
              onClick={() => handleCardClick(i)}
            />
          ))}
        </div>
      </motion.div>

      {/* Swipe hint */}
      <p className="text-center text-zinc-500 text-[10px] tracking-widest uppercase mt-3 mb-1" data-testid="swipe-hint">
        ← swipe to explore →
      </p>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-1">
        {communities.map((c, i) => (
          <button
            key={c.slug}
            onClick={() => setCenterIndex(i)}
            className="w-2 h-2 rounded-full transition-all"
            style={{
              backgroundColor: i === centerIndex ? (tarotStyles[c.slug]?.accent || '#fff') : 'rgb(63,63,70)',
              transform: i === centerIndex ? 'scale(1.3)' : 'scale(1)',
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default CommunitiesRow;
